/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { DEFAULT_ERRORS_STATE } from '../hooks/components/useChangePassword';

interface PasswordValidationParams {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  translations: Record<string, string>;
}

interface PasswordErrors {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const validationChangePassword = ({
  currentPassword,
  newPassword,
  confirmPassword,
  translations,
}: PasswordValidationParams): { isValid: boolean; errors: PasswordErrors } => {
  let errors: PasswordErrors = { ...DEFAULT_ERRORS_STATE };

  const arePasswordsEmpty =
    !currentPassword.length && !newPassword.length && !confirmPassword.length;
  const isNewPasswordMismatch =
    currentPassword.length && newPassword !== confirmPassword;

  if (arePasswordsEmpty) {
    errors = {
      ...errors,
      currentPassword: translations.requiredFieldError,
      newPassword: translations.requiredFieldError,
      confirmPassword: translations.requiredFieldError,
    };
    return { isValid: false, errors };
  }

  if (!currentPassword.length) {
    errors = {
      ...errors,
      currentPassword: translations.requiredFieldError,
    };
    return { isValid: false, errors };
  }

  if (!newPassword.length) {
    errors = {
      ...errors,
      newPassword: translations.requiredFieldError,
    };
    return { isValid: false, errors };
  }

  if (!confirmPassword.length) {
    errors = {
      ...errors,
      confirmPassword: translations.requiredFieldError,
    };
    return { isValid: false, errors };
  }

  if (isNewPasswordMismatch) {
    errors = {
      ...errors,
      currentPassword: '',
      newPassword: '',
      confirmPassword: translations.passwordMismatch,
    };
    return { isValid: false, errors };
  }

  return { isValid: true, errors };
};
